import { RoutePath } from '@/src/RoutePath';
import { SelectInput } from '@/src/components/common/SelectInput/SelectInput';
import { config } from '@/src/config/app-config';
import { EventAudienceType } from '@/src/constants/shared';
import { useEventsApi } from '@/src/hooks/api/useEventsApi';
import useDebounceCallback from '@/src/hooks/useDebounceCallback';
import { useQuery } from '@/src/hooks/useQuery';
import { UserPreferenceKeys, useUser } from '@/src/store/user.context';
import {
  Box,
  Button,
  Checkbox,
  CollectionPreferences,
  CollectionPreferencesProps,
  FormField,
  Input,
  Link,
  Pagination,
  RadioGroup,
  SpaceBetween,
  Table,
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useHistory } from 'react-router-dom';
import '../../styles/base.scss';
import { Event, EventStatus } from '../../types/Event';
import { ApprovalStatus, ChangeRequestStatus, EventFilterType, EventListFilter } from '../../types/common';
import { YYYY_MM_DD } from '../../utils/event-time.utils';
import { i18nKeys } from '../../utils/i18n.utils';
import { paginationLabels } from '../../utils/table.utils';
import { DateTimeKeys, TimezoneFormat } from '../common/CommonModel';
import { DateRangeFilterInput } from '../common/DateRangeFilterInput/DateRangeFilterInput';
import { MultiSelectInput } from '../common/MultiSelectInput/MultiSelectInput';
import { TableEmptyState } from '../common/TableEmptyState';
import { TableHeader } from '../common/TableHeader';
import './EventList.scss';
import { TemplateUrlModal } from './TemplateUrlModal';
import { COLUMN_DEFINITIONS } from './event-list-config';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { SortingColumn } from '@amzn/awsui-collection-hooks/cjs/interfaces';

enum QueryParams {
  DateStart = 'startDateRange',
  DateEnd = 'endDateRange',
  Channel = 'channel',
  Type = 'type',
  Status = 'status',
  IncludeEndedEvents = 'includeEndedEvents',
}

const defaultPageSize = 30;

const defaultStartDate = moment().subtract(30, 'days').format(YYYY_MM_DD);

const getChannelFilterLabelKey = (channel: keyof typeof EventAudienceType) => {
  switch (channel) {
    case EventAudienceType.INTERNAL:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.internal;
    case EventAudienceType.GENERAL_PUBLIC:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.generalPublic;
    case EventAudienceType.AWS_PRIVATE_ENGAGEMENT:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.awsPrivateEngagement;
    case EventAudienceType.AWS_BOOTCAMP:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.awsBootCamp;
    case EventAudienceType.AWS_INTERNAL:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.awsInternal;
    case EventAudienceType.CUSTOMER_INTERNAL:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.customerInternal;
    case EventAudienceType.TESTERS:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.testers;
    case EventAudienceType.INDIVIDUAL:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.individual;
    case EventAudienceType.SKILL_BUILDER_EVENT:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.skillBuilderEvent;
    case EventAudienceType.AWS_CLASSROOMS_BUILDER_LABS:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.awsClassroomsBuilderLabs;
    case EventAudienceType.PUBLIC:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.public;
    case EventAudienceType.PAID_ALA_CARTE:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.paidAlaCarte;
    case EventAudienceType.GLOBAL_PARTNERS:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.globalPartners;
    case EventAudienceType.AWS_EDUCATION:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.awsEducation;
    case EventAudienceType.RECRUITING:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.recruiting;
    case EventAudienceType.ACI_EVENT:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.aciEvent;
    case EventAudienceType.SKILL_BUILDER_LEARNER_EVENT:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.sbtsLearnerEvent;
    case EventAudienceType.SUMMIT:
      return i18nKeys.events.eventDetails.labels.eventAudienceType.summit;
  }

  return 'unknown';
};

const EventList: React.FC = () => {
  const { t } = useTranslation();
  const eventsApi = useEventsApi();
  const history = useHistory();
  const { user } = useUser();
  const { queryMap } = useQuery();
  const isMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const [templateModalVisible, setTemplateModalVisible] = useState(false);

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: defaultPageSize,
    custom: TimezoneFormat.LOCAL,
  });

  const parseQueryList = (list?: string) => (list ? list.split(',') : []);

  const [titleFilter, setTitleFilter] = useState('');

  const [startDateRange, setStartDateRange] = useState(queryMap[QueryParams.DateStart] || defaultStartDate);
  const [endDateRange, setEndDateRange] = useState(queryMap[QueryParams.DateEnd] || '');
  const [typeFilter, setTypeFilter] = useState(parseQueryList(queryMap[QueryParams.Type]));
  const [statusFilter, setStatusFilter] = useState(parseQueryList(queryMap[QueryParams.Status]));
  const [includeEndedEvents, setIncludeEndedEvents] = useState(queryMap[QueryParams.IncludeEndedEvents] === 'true');
  const [channelFilter, setChannelFilter] = useState(queryMap[QueryParams.Channel]);
  const [sortingColumn, setSortingColumn] = useState<SortingColumn<Event> | undefined>();
  const sortField = sortingColumn?.sortingField;
  const [ascending, setAscending] = useState<boolean | null | undefined>();

  const [events, setEvents] = useState<Event[]>([]);
  const [eventsCount, setEventsCount] = useState(0);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [currPageIndex, setCurrPageIndex] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    setCurrPageIndex(1);
  }, [pageCount]);

  useEffect(() => {
    const searchParams = new URLSearchParams();

    searchParams.set(QueryParams.DateStart, startDateRange);
    searchParams.set(QueryParams.DateEnd, endDateRange || '');
    searchParams.set(QueryParams.Type, typeFilter.toString());
    searchParams.set(QueryParams.IncludeEndedEvents, includeEndedEvents.toString());
    searchParams.set(QueryParams.Channel, channelFilter || '');
    searchParams.set(QueryParams.Status, statusFilter.toString());

    Object.values(QueryParams).forEach((param) => (!searchParams.get(param) ? searchParams.delete(param) : null));

    history.replace({ search: searchParams.toString() });
  }, [startDateRange, endDateRange, typeFilter, includeEndedEvents, channelFilter, statusFilter]);

  const getLoadEventsFilter = () => ({
    page: currPageIndex,
    limit: preferences.pageSize,
    filter: typeFilter.toString(),
    status: statusFilter.toString(),
    includeEndedEvents,
    channel: channelFilter,
    start: startDateRange,
    end: endDateRange || null,
    title: titleFilter,
    sortField,
    ascending,
    excludeFields: ['challengeDescriptors', 'eventFullMessage'],
  });

  const loadEvents = async (filter: EventListFilter) => {
    setLoadingEvents(true);

    try {
      const getEventsResponse = await eventsApi.getEvents(filter);

      setEvents(getEventsResponse.events);
      setEventsCount(getEventsResponse.count);
      setPageCount(Math.ceil(getEventsResponse.count / (preferences.pageSize || defaultPageSize)));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('error fetching events', e);
    } finally {
      setLoadingEvents(false);
      isInitialLoad.current = false;
    }
  };

  const debounceLoadEvents = useDebounceCallback((filter: EventListFilter) => loadEvents(filter), 1000);

  useEffect(() => {
    if (isInitialLoad.current) {
      return;
    }

    setLoadingEvents(true);

    setEvents([]);
    setEventsCount(0);
    setPageCount(1);

    debounceLoadEvents(getLoadEventsFilter());
  }, [titleFilter]);

  useEffect(() => {
    loadEvents(getLoadEventsFilter());
  }, [
    currPageIndex,
    startDateRange,
    endDateRange,
    typeFilter,
    includeEndedEvents,
    channelFilter,
    statusFilter,
    sortField,
    ascending,
  ]);

  const channelFilterOptions = Object.keys(EventAudienceType).map((value) => ({
    label: t(getChannelFilterLabelKey(value as keyof typeof EventAudienceType)),
    value,
  }));

  const typeFilterOptions = [
    {
      label: t(i18nKeys.events.fields.filters.eventTypes.jamEvents),
      value: EventFilterType.JAM,
    },
    {
      label: t(i18nKeys.events.fields.filters.eventTypes.campaignEvents),
      value: EventFilterType.CAMPAIGN,
    },
  ];

  const statusFilterOptions = [
    {
      label: t(i18nKeys.events.fields.filters.status.label),
      options: [
        {
          label: t(i18nKeys.events.fields.filters.status.notStarted),
          value: EventStatus.NOT_STARTED,
        },
        {
          label: t(i18nKeys.events.fields.filters.status.ongoing),
          value: EventStatus.ONGOING,
        },
        {
          label: t(i18nKeys.events.fields.filters.status.ended),
          value: EventStatus.ENDED,
        },
        {
          label: t(i18nKeys.events.fields.filters.status.cancelled),
          value: EventStatus.CANCELLED,
        },
      ],
    },
    {
      label: t(i18nKeys.events.fields.filters.approvalStatus.label),
      options: [
        {
          label: t(i18nKeys.events.fields.filters.approvalStatus.requestSubmitted),
          value: ApprovalStatus.REQUEST_SUBMITTED,
        },
        {
          label: t(i18nKeys.events.fields.filters.approvalStatus.requestPending),
          value: ApprovalStatus.REQUEST_PENDING,
        },
        {
          label: t(i18nKeys.events.fields.filters.approvalStatus.requestCancelled),
          value: ApprovalStatus.REQUEST_CANCELLED,
        },
        {
          label: t(i18nKeys.events.fields.filters.approvalStatus.requestDenied),
          value: ApprovalStatus.REQUEST_DENIED,
        },
        {
          label: t(i18nKeys.events.fields.filters.approvalStatus.changeRequested),
          value: ChangeRequestStatus.CHANGE_REQUESTED,
        },
        {
          label: t(i18nKeys.events.fields.filters.approvalStatus.changePending),
          value: ChangeRequestStatus.CHANGE_PENDING,
        },
      ],
    },
  ];

  const handleOnLoadPreferences = () => {
    const pageSize = Number(sessionStorage.getItem(UserPreferenceKeys.PAGE_SIZE));
    const displayTime: string | null = sessionStorage.getItem(UserPreferenceKeys.DISPLAY_TIME);

    const userPreferences = {
      pageSize: pageSize ? pageSize : preferences.pageSize,
      custom: displayTime ? displayTime : preferences.custom,
    };

    setPreferences(userPreferences);
  };

  useEffect(() => {
    // TODO: Load filtering from memory JAM-2234
    handleOnLoadPreferences();
  }, []);

  const handleDateSelection = (startOrEnd: DateTimeKeys, date: any): void => {
    if (startOrEnd === DateTimeKeys.START) {
      setStartDateRange(formatDate(startOrEnd, date) || startDateRange);
    } else {
      setEndDateRange(formatDate(startOrEnd, date) || endDateRange);
    }
  };

  const formatDate = (startOrEnd: DateTimeKeys, date: any) => {
    return date && moment(date).format(YYYY_MM_DD) !== 'Invalid date'
      ? moment(date).format(YYYY_MM_DD)
      : startOrEnd === DateTimeKeys.START
      ? moment().subtract(30, 'days').format(YYYY_MM_DD)
      : undefined;
  };

  const handleTypeSelection = (selectedTypes: readonly OptionDefinition[]) => {
    const newTypes: string[] = [];

    for (const status of selectedTypes) {
      if (status.value) {
        newTypes.push(status.value);
      }
    }

    setTypeFilter(newTypes);
  };

  const handleStatusSelection = (selectedStatuses: readonly OptionDefinition[]) => {
    const newStatuses: string[] = [];

    for (const status of selectedStatuses) {
      if (status.value) {
        newStatuses.push(status.value);
      }
    }

    if (newStatuses.includes(EventStatus.ENDED)) {
      setIncludeEndedEvents(true);
    }

    setStatusFilter(newStatuses);
  };

  const handleSetPreferences = (newPreferences: CollectionPreferencesProps.Preferences) => {
    if (newPreferences.pageSize) {
      sessionStorage.setItem(UserPreferenceKeys.PAGE_SIZE, newPreferences.pageSize.toString());
    }

    if (newPreferences.custom) {
      sessionStorage.setItem(UserPreferenceKeys.DISPLAY_TIME, newPreferences.custom);
    }

    setPreferences(newPreferences);
  };

  const { collectionProps, items } = useCollection(events, {
    sorting: {},
  });

  return (
    <div>
      <TemplateUrlModal visible={templateModalVisible} onDismiss={() => setTemplateModalVisible(false)} />
      <Table
        variant="full-page"
        stickyHeader
        {...collectionProps}
        empty={
          user?.hasBasicAccess ? (
            <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <b>{t(i18nKeys.events.list.empty.description)}</b>
              </SpaceBetween>
            </Box>
          ) : (
            <TableEmptyState
              title={t(i18nKeys.events.list.empty.title)}
              subtitle={t(i18nKeys.events.list.empty.subtitle)}
            />
          )
        }
        loading={loadingEvents}
        header={
          <TableHeader
            totalItems={eventsCount}
            title={t(i18nKeys.events.title)}
            actionButtons={
              <SpaceBetween direction={isMobile ? 'vertical' : 'horizontal'} size="xs">
                {!isMobile && (
                  <DateRangeFilterInput
                    direction="horizontal"
                    dateRangeFilter={{ start: startDateRange, end: endDateRange || null }}
                    handleDateSelection={handleDateSelection}
                    className="date-selector-action-button"
                  />
                )}
                {(user?.isSuperAdmin || user?.isEventAdmin) && (
                  <Button variant="primary" href={RoutePath.NEW_EVENT}>
                    {t(i18nKeys.newEvent.title)}
                  </Button>
                )}
                {user?.isOnlyBasicUser && (
                  <Button>
                    <Link href={config.billing.purchaseHistory} external>
                      {t(i18nKeys.events.transactionHistory)}
                    </Link>
                  </Button>
                )}
              </SpaceBetween>
            }
          />
        }
        columnDefinitions={COLUMN_DEFINITIONS(preferences)}
        items={items}
        pagination={
          <Pagination
            currentPageIndex={currPageIndex}
            onChange={({ detail }) => setCurrPageIndex(detail.currentPageIndex)}
            pagesCount={pageCount}
            ariaLabels={paginationLabels(t)}
          />
        }
        preferences={
          <CollectionPreferences
            title={t(i18nKeys.events.fields.preferences.title)}
            onConfirm={({ detail }) => handleSetPreferences(detail)}
            confirmLabel={t(i18nKeys.general.confirm)}
            cancelLabel={t(i18nKeys.general.cancel)}
            preferences={preferences}
            customPreference={(customValue, setCustomValue) => (
              <FormField label={t(i18nKeys.events.fields.filters.timezoneOptions.label)}>
                <RadioGroup
                  value={customValue}
                  onChange={({ detail }) => setCustomValue(detail.value)}
                  items={[
                    { value: TimezoneFormat.LOCAL, label: t(i18nKeys.events.fields.filters.timezoneOptions.local) },
                    { value: TimezoneFormat.UTC, label: t(i18nKeys.events.fields.filters.timezoneOptions.utc) },
                  ]}
                />
              </FormField>
            )}
            pageSizePreference={{
              title: t(i18nKeys.events.fields.preferences.pageSize.title),
              options: [
                { value: 10, label: t(i18nKeys.events.fields.preferences.pageSize.label, { count: 10 }) },
                { value: 20, label: t(i18nKeys.events.fields.preferences.pageSize.label, { count: 20 }) },
                { value: 30, label: t(i18nKeys.events.fields.preferences.pageSize.label, { count: 30 }) },
                { value: 50, label: t(i18nKeys.events.fields.preferences.pageSize.label, { count: 50 }) },
                { value: 100, label: t(i18nKeys.events.fields.preferences.pageSize.label, { count: 100 }) },
              ],
            }}
          />
        }
        filter={
          <Box margin={{ top: 'xs', bottom: 'xs' }}>
            <div className="event-list-select">
              <SpaceBetween direction={isMobile ? 'vertical' : 'horizontal'} size="m">
                <SpaceBetween size="s">
                  <div>
                    <span>{t(i18nKeys.events.fields.filters.title.label)}</span>
                    <Input
                      value={titleFilter}
                      onChange={(e) => setTitleFilter(e.detail.value)}
                      placeholder={t(i18nKeys.events.fields.filters.title.placeholder)}
                      type="search"
                    />
                  </div>
                  <span className="event-list-checkbox-filter">
                    <Checkbox
                      checked={includeEndedEvents}
                      onChange={({ detail }) => setIncludeEndedEvents(detail.checked)}
                    >
                      {t(i18nKeys.events.fields.filters.labels.showEndedEvents)}
                    </Checkbox>
                  </span>
                </SpaceBetween>
                {isMobile && (
                  <DateRangeFilterInput
                    direction="vertical"
                    dateRangeFilter={{ start: startDateRange, end: endDateRange || null }}
                    handleDateSelection={handleDateSelection}
                    className="date-selector-filter"
                  />
                )}
                <SelectInput
                  allowNone
                  options={channelFilterOptions}
                  label={t(i18nKeys.events.fields.filters.channel.label)}
                  selectedOption={channelFilterOptions.find((option) => option.value === channelFilter)}
                  placeholder={t(i18nKeys.events.fields.filters.channel.placeholder)}
                  onChange={(event) => setChannelFilter(event.detail.selectedOption?.value)}
                />
                <MultiSelectInput
                  options={typeFilterOptions}
                  label={t(i18nKeys.events.fields.filters.eventTypes.label)}
                  selectedOptions={typeFilterOptions.filter((option) => typeFilter.includes(option.value))}
                  placeholder={t(i18nKeys.events.fields.filters.eventTypes.placeholder)}
                  onChange={(event) => handleTypeSelection(event.detail.selectedOptions)}
                />
                <MultiSelectInput
                  options={statusFilterOptions}
                  label={t(i18nKeys.events.fields.filters.status.label)}
                  selectedOptions={statusFilterOptions
                    .flatMap((group) => group.options as OptionDefinition[])
                    .filter((option) => statusFilter.includes(option.value ?? ''))}
                  placeholder={t(i18nKeys.events.fields.filters.status.placeholder)}
                  onChange={(event) => handleStatusSelection(event.detail.selectedOptions)}
                />
              </SpaceBetween>
            </div>
          </Box>
        }
        stickyColumns={{ first: 0 }}
        onSortingChange={({ detail }) => {
          setSortingColumn(detail.sortingColumn);
          setAscending(!detail.isDescending);
        }}
        sortingDescending={!ascending}
        sortingColumn={sortingColumn}
      />
    </div>
  );
};

export default EventList;
